import { RealityCapturePoint } from '@typings/reality-capture/common';
import { DemCrossSectionInput } from '@typings/reality-capture/dem/types';
import { PointCloudPoint } from '@pages/cross-section/cross-section/CrossSectionStore';
import { getRasterImage, getWindowFromBBox, rasterToPoints, uuidtoUrl, legacyUuidtoUrl, filterPoints, downsamplePoints, RcPointsToCrossSectionModel } from './utils/dem-utils';
import { RcPointsToCrossSectionPoints, areaToBbox, getRandomPastelColor } from './utils/common';
import { FileTreeService } from '@shared/services/FileTreeService';
import { ICrossSectionModel } from '@pages/cross-section/shared/CrossSectionWindow';

export class DemStore {
    private treeData: any = null;
    private tileLayers: any = null;
    private demColorCache = new Map<string, string>();

    constructor() {
        FileTreeService.fetchFileTree().then(data => {
            this.treeData = data;
        });
    }

    setTileLayers(tileLayers: any) {
        this.tileLayers = tileLayers;
    }

    getActiveDemFileUuids(): string[] {
        return this.tileLayers?.layersDefinitions?.filter(l => !!l?.demUrl && l?.activeUuids?.length)?.flatMap(l => l.activeUuids) || [];
    }

    async getCrossSectionData(input: DemCrossSectionInput) {
        const results = await Promise.all(
            input.includeDemUuids.map(async (uuid, index) => {
                try {
                    console.log('DEM uuid for cross section', uuid);

                    const color = index === 0 ? '255,0,0' : (
                        this.demColorCache.get(uuid) ?? (() => {
                            const c = getRandomPastelColor();
                            this.demColorCache.set(uuid, c);
                            return c;
                        })()
                    );

                    // Filter points inside region
                    const rcPoints: RealityCapturePoint[] = await this.getPointsInsideRegion(input.area, uuid);
                    const filtered = filterPoints(rcPoints, input.startVector, input.endVector, input.rectangleWidth);
                    const downsampled = downsamplePoints(filtered, 150);

                    // Convert points to cross section points
                    const points: PointCloudPoint[] = await RcPointsToCrossSectionPoints({
                        ...input,
                        points: downsampled,
                        color,
                    });

                    // Convert points to cross section model
                    const model: ICrossSectionModel = RcPointsToCrossSectionModel({ points: points, color, uuid });

                    return { points, model, uuid };
                } catch (error) {
                    console.error(`Error processing DEM for uuid ${uuid}:`, error);
                    return null;
                }
            })
        );

        const valid = results.filter(Boolean);
        return {
            DemModels: valid.map(r => r!.model),
            DemPoints: valid.flatMap(r => r!.points),
            DemUuids: valid.map(r => r!.uuid),
        };
    }

    getFileNameFromUuid(uuid: string): string {
        const file = FileTreeService.findNodeByUuid(this.treeData, uuid);
        return file ? file.name : uuid;
    }

    getModelIdByUuid(uuid: string): number | null {
        const node = FileTreeService.findNodeByUuid(this.treeData, uuid);
        return typeof node?.id === 'number' ? node.id : null;
    }

    async getPointsInsideRegion(region: number[][], uuid: string): Promise<RealityCapturePoint[]> {
        const bbox = areaToBbox(region);
        const name = this.getFileNameFromUuid(uuid);
        const sourceUrl = uuidtoUrl(uuid);
        const legacySourceUrl = legacyUuidtoUrl(uuid);

        let image;
        try {
            image = await getRasterImage(sourceUrl);
        } catch (error) {
            image = await getRasterImage(legacySourceUrl);
        }
        
        const window = getWindowFromBBox(image, bbox);
        const raster = await image.readRasters({ window });

        return await rasterToPoints(name, uuid, raster, image, window);
    }
}

export const demStore = new DemStore();
